import { PagingResponse } from '@/@types/responses/common/PagingResponse'
import { AppConfig } from '@/app.config'
import authenticatedFetch from '@/authenticatedFetch'
import { InventoryType } from '@/@types/responses/inventory_type/InventoryType'
import { InventoryTypeDetails } from '@/@types/responses/inventory_type/InventoryTypeDetails'
import { InventoryTypeDropdownOption } from '@/@types/responses/inventory_type/InventoryTypeDropdownOption'
import { DropdownOptionsResponse } from '@/@types/responses/common/DropdownOptionsResponse'
import { InventoryTypeMessage } from '@/@types/messages/InventoryTypeMessage'
import { CreateInventoryTypeFieldMessage } from '@/@types/messages/CreateInventoryTypeFieldMessage'
import { MiscellaneousService } from './miscellaneous.service'
import { ObjectCreatedResponse } from '@/@types/responses/common/ObjectCreatedResponse'

export class InventoryTypeService {
  private appConfig: AppConfig
  private miscellaneousService: MiscellaneousService

  constructor(miscellaneousService: MiscellaneousService) {
    this.appConfig = new AppConfig()
    this.miscellaneousService = miscellaneousService
  }

  public createInventoryType(inventoryTypeMessage: InventoryTypeMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(inventoryTypeMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/inventory-types`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createInventoryTypeField(inventoryTypeId: number, createInventoryTypeFieldMessage: CreateInventoryTypeFieldMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createInventoryTypeFieldMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/inventory-types/${inventoryTypeId}/inventory-type-fields`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public deleteInventoryType(id: number): Promise<void> {
    const requestOptions = {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/inventory-types/${id}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public getInventoryType(id: number): Promise<InventoryTypeDetails> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/inventory-types/${id}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return new InventoryTypeDetails(json.id, json.displayOrder, json.name, json.inventoryTypeFields, json.createdByUser, json.createdDate, json.lastModifiedByUser, json.lastModifiedDate)
    })
  }

  public getInventoryTypeDropdownOptions(supplierId: number | undefined): Promise<DropdownOptionsResponse<InventoryTypeDropdownOption>> {
    let route = `${this.appConfig.sacServicesUrl}/v1/inventory-types/dropdown-options`

    if (supplierId != undefined)
      route += '?supplierId=' + supplierId

    return authenticatedFetch(route, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as DropdownOptionsResponse<InventoryTypeDropdownOption>
    })
  }

  public searchInventoryTypes(searchTerm: string, pageNumber: number, pageSize: number, sortField: string, ascending: boolean): Promise<PagingResponse<InventoryType>> {
    const queryString = `?searchTerm=${searchTerm}&pageNumber=${pageNumber}&pageSize=${pageSize}&sortField=${sortField}&ascending=${ascending}`

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/inventory-types${queryString}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as PagingResponse<InventoryType>
    })
  }

  public updateInventoryType(inventoryTypeId: number, inventoryTypeMessage: InventoryTypeMessage): Promise<void> {
    const requestOptions = {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(inventoryTypeMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/inventory-types/${inventoryTypeId}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }
}
